import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { CommonEnums, DataStorageEnums, ReduxService, TimeService } from 'dashboard-services';

import { creatorSeriesActions, settingsSeriesActions } from 'actions/series';
import FORMULAS from 'resources/constants/Formulas.js';
import { FormulaUtils, GAUtils, TimeSeriesUtils } from 'utils';

import BasketNew from 'primary/Basket';
import BasketHeader from 'primary/BasketHeader';
import FormulaAdded from 'primary/FormulaAdded';
import TooLongWarn from 'primary/TooLongWarn';
import DataTab from './DataTab';
import Settings from './Settings';

import dayjs from 'dayjs';

import { When } from 'react-if';

const emptyArray = [],
      addAndValidateParam = FormulaUtils.addAndValidateParam
const BasketLocal = ({ isStartingResults, symbols, totalSize }) => {
  const dispatch = useDispatch(),
        defaultSource = useSelector(state => state.globalState.source),
        [showTooLongFormula, setShowTooLongFormula] = useState(false),
        address = useSelector(state => state.seriesState.creatorState.address),
        [addressLocal, setAddressLocal] = useState(),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols) || emptyArray,
        selectedColumns = useSelector(state => state.seriesState.creatorState.selectedColumns) || emptyArray,
        isBasketOpen = useSelector(state => state.seriesState.creatorState.isBasketOpen),
        swapIsOpen = useCallback(() => dispatch(creatorSeriesActions.onChange(!isBasketOpen, "isBasketOpen")), [dispatch, isBasketOpen]),
        addToSheet = useCallback(async () => {
          addressLocal && setAddressLocal()
          GAUtils.sendEvent({
            category: "action",
            action: "add_ts",
            label: "add"
          })
          const paramsExceedingLength = []
          let formula = `=${FORMULAS.LOAD_TS}(`
          selectedSymbols.forEach(({ groupName, symbols = {}, metadata = {}, pattern = "true", query, columns = [], source = undefined }, index) => {
            const columnsToUse = columns && columns?.length > 0 ? columns : selectedColumns
            formula += addAndValidateParam({ paramName: "groupName", paramValue: groupName || "", paramsExceedingLength, withComma: index > 0 })
            formula += addAndValidateParam({ paramName: "columns", paramValue: columnsToUse.join(","), paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "symbols", paramValue: `""{${Object.entries(symbols).map(([key, value]) => `""${key}"":""${value}""`).join(", ")}}""`, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "metadatas", paramValue: `""{${Object.entries(pattern === "true" ? metadata : {}).map(([key, value]) => `""${key}"":""${value}""`).join(", ")}}""`, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "pattern", paramValue: pattern, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "query", paramValue: query || "", paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "source", paramValue: source || defaultSource, paramsExceedingLength })
          })
          const { timeZone, range, from, to, fromSelected, toSelected, lastType, lastTypeAmount, dateFormat, order, formatType, csvHeaders, shouldTranspose, sortKeys = "false", sortKeysBy, sortKeysOrder = CommonEnums.SORT_ORDER.ASCENDING } = dispatch(creatorSeriesActions.getParams())
          if(timeZone) {
            formula += addAndValidateParam({ paramName: "timeZone", paramValue: timeZone, paramsExceedingLength })
          }
          if(dateFormat) {
            formula += addAndValidateParam({ paramName: "dateFormat", paramValue: dateFormat, paramsExceedingLength })
          }
          if(range) {
            formula += addAndValidateParam({ paramName: "range", paramValue: range, paramsExceedingLength })
          }
          if(range === DataStorageEnums.DATA_RANGE.BETWEEN && (from || to)) {
            if(from) {
              const startDateValue = fromSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && from ? dayjs(from + "T00:00:00.000").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined
              formula += addAndValidateParam({ paramName: "startDate", paramValue: startDateValue, paramsExceedingLength })
            }
            if(to) {
              const endDateValue = toSelected === DataStorageEnums.DATE_BETWEEN_TYPE.DATE && to ? dayjs(to  + "T23:59:59.999").format(TimeService.ISO_DATE_TIME_FORMAT_BACKEND_WITH_SECONDS_STYLE) : undefined
              formula += addAndValidateParam({ paramName: "endDate", paramValue: endDateValue, paramsExceedingLength })
            }
          }
          if(range === DataStorageEnums.DATA_RANGE.LAST || range === DataStorageEnums.DATA_RANGE.NEXT) {
            formula += addAndValidateParam({ paramName: "lastType", paramValue: lastType, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "lastTypeAmount", paramValue: lastTypeAmount, paramsExceedingLength })
          }
          if(order) {
            formula += addAndValidateParam({ paramName: "order", paramValue: order, paramsExceedingLength })
          }
          if(formatType) {
            formula += addAndValidateParam({ paramName: "formatType", paramValue: formatType, paramsExceedingLength })
          }
          if(csvHeaders) {
            const prepColumHeader = FormulaUtils.prepColumnHeaders({ csvHeaders, formatType })
            formula += addAndValidateParam({ paramName: "csvHeaders", paramValue: `""[${prepColumHeader}]""`, paramsExceedingLength })
          }
          if(sortKeys === "true") {
            formula += addAndValidateParam({ paramName: "sortKeys", paramValue: sortKeys, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "sortKeysBy", paramValue: `""{${Object.entries(sortKeysBy || {}).map(([key, value]) => `""${key}"":""${value}""`).join(", ")}}""`, paramsExceedingLength })
            formula += addAndValidateParam({ paramName: "sortKeysOrder", paramValue: sortKeysOrder, paramsExceedingLength })
          }
          if(shouldTranspose !== undefined) {
            formula += addAndValidateParam({ paramName: "shouldTranspose", paramValue: shouldTranspose, paramsExceedingLength })
          }
          formula += ")"
          const setAddress = addressLocal => dispatch(creatorSeriesActions.onChange(addressLocal, "address"))
          FormulaUtils.formulaValidator({ formula, paramsExceedingLength, address, setShowTooLongFormula, setAddress: address ? setAddress : setAddressLocal })
        }, [address, addressLocal, dispatch, defaultSource, selectedColumns, selectedSymbols]),
        onReset = useCallback(() => {
          dispatch(creatorSeriesActions.clear())
          dispatch(settingsSeriesActions.clear())
        }, [dispatch]),
        onClearSelected = useCallback(() => dispatch(creatorSeriesActions.onChange([], "selectedSymbols")), [dispatch]),
        onChangeColumns = useCallback(value => dispatch(creatorSeriesActions.onChange(value, "selectedColumns")), [dispatch]),
        hideWarns = useCallback(() => setShowTooLongFormula(false), []),
        closeFormulaAdded = useCallback(() => setAddressLocal(), []),
        onEdit = useCallback(async () => await Excel.run(context => {
          const cell = context.workbook.worksheets
            .getActiveWorksheet()
            .getRange(addressLocal);
          cell.load("formulas");
          cell.load("address");
          return context.sync().then(async () => {
              const formula = cell.formulas?.[0]?.[0],
                    tsParsed = await TimeSeriesUtils.validateTable({
                      cell,
                      formula,
                      returnItem: true
                    })
              if(tsParsed) {
                const groupedParams = tsParsed.parsedParameters.getGroupedParams()
                dispatch(creatorSeriesActions.editFormula({ 
                  address: tsParsed.address, 
                  keys: groupedParams.map(gp => ReduxService.updateObject(gp, { columns: gp.columns?.split(",") })),
                  params: tsParsed.parsedParameters
                }))
                closeFormulaAdded()
              }
          })
        }), [addressLocal, closeFormulaAdded, dispatch]),
        arePatternsSelected = useMemo(() => selectedSymbols.some(s => s.pattern === "true" || s.pattern === undefined), [selectedSymbols]),
        counterLabel = useMemo(() => {
          if (!arePatternsSelected) {
            return 'selected'
          }
          if(selectedSymbols?.length > 1) {
            return "patterns";
          } else {
            return "pattern";
          }
        }, [arePatternsSelected, selectedSymbols])

  useEffect(() => {
    if(addressLocal) {
      onReset()
    }
  }, [addressLocal, onReset])

  return (
    <Fragment>
      <When condition={addressLocal}>
        {() => (
          <FormulaAdded 
              addressLocal={addressLocal}
              onClose={closeFormulaAdded}
              onEdit={onEdit}
          />
        )}
      </When>
      <BasketHeader
          addToSheet={addToSheet}
          counterLabel={counterLabel}
          isStartingResults={isStartingResults}
          onChangeColumns={onChangeColumns}
          onClearSelected={onClearSelected}
          onConfigure={swapIsOpen}
          onReset={onReset}
          selectedColumns={selectedColumns}
          selectedItems={selectedSymbols}
          symbols={symbols}
          totalSize={totalSize}
      />
      <BasketNew
          address={address}
          addToSheet={addToSheet}
          counterLabel={counterLabel}
          dataTab={<DataTab/>}
          isBasketOpen={isBasketOpen}
          onClearSelected={onClearSelected}
          onReset={onReset}
          selectedItems={selectedSymbols}
          settings={<Settings/>}
          swapIsOpen={swapIsOpen}
      />
      <When condition={showTooLongFormula}>
        {() => (
          <TooLongWarn onClose={hideWarns}/>
        )}
      </When>
    </Fragment>
  )
}

BasketLocal.defaultProps = {
  symbols: [],
  totalSize: 0,
}

BasketLocal.propTypes = {
  isStartingResults: PropTypes.bool.isRequired,
  symbols: PropTypes.array,
  totalSize: PropTypes.number,
}

export default BasketLocal;